import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { ClipboardList, Download, Bell, Trash2, ChevronLeft, CheckCircle2, Clock, UserCheck, Vote, FileDown, Mail } from "lucide-react";
import { ComunicazioniModelli } from "../components/ComunicazioniModelli";

export default function RaccoltaDati() {
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);     // dettaglio campagna
  const [busy, setBusy] = useState(false);
  const [fTipo, setFTipo] = useState("");
  const [fCond, setFCond] = useState("");
  const [applyDest, setApplyDest] = useState(null);   // destinatario in revisione
  const [applyMeta, setApplyMeta] = useState(null);   // {tipo, fields, current, record_found}
  const [mapping, setMapping] = useState({});         // campo_id -> anagrafica field key
  const [applying, setApplying] = useState(false);
  const [sp] = useSearchParams();

  const load = () => api.get("/raccolte").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const apri = (id) => api.get(`/raccolte/${id}`).then((r) => { setSel(r.data); setFTipo(""); setFCond(""); }).catch(() => {});

  // Iter122aa · auto-open raccolta se ?rid= presente in URL (link da PreventivoDetail)
  useEffect(() => {
    const rid = sp.get("rid");
    if (rid && !sel) apri(rid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);

  const openApply = async (d) => {
    try {
      const { data } = await api.get(`/raccolte/${sel.id}/destinatario/${d.id}/anagrafica`);
      setApplyMeta(data); setApplyDest(d); setMapping({});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Impossibile caricare l'anagrafica"); }
  };

  const applyNow = async () => {
    const updates = {};
    for (const [campoId, field] of Object.entries(mapping)) {
      if (!field) continue;
      updates[field] = (applyDest.risposte || {})[campoId] ?? "";
    }
    if (Object.keys(updates).length === 0) return toast.error("Associa almeno un campo all'anagrafica");
    setApplying(true);
    try {
      const { data } = await api.post(`/raccolte/${sel.id}/destinatario/${applyDest.id}/applica`, { updates });
      toast.success(`Anagrafica aggiornata (${data.aggiornati.length} campi)`);
      setApplyDest(null); setApplyMeta(null); setMapping({});
      apri(sel.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Aggiornamento non riuscito"); }
    finally { setApplying(false); }
  };

  const esporta = async (id, titolo) => {
    try {
      const res = await api.get(`/raccolte/${id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `raccolta_${(titolo || "dati").replace(/\s+/g, "_")}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Esportazione non riuscita"); }
  };

  const sollecita = async (id) => {
    setBusy(true);
    try { const { data } = await api.post(`/raccolte/${id}/sollecita`); toast.success(`Sollecito inviato a ${data.sollecitati} destinatari mancanti.`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
    finally { setBusy(false); }
  };

  const elimina = async (id) => {
    if (!window.confirm("Eliminare questa raccolta e tutte le risposte?")) return;
    try { await api.delete(`/raccolte/${id}`); toast.success("Raccolta eliminata"); setSel(null); load(); }
    catch { toast.error("Eliminazione non riuscita"); }
  };

  // Iter122jl · Invio manuale email di riepilogo esito ai destinatari (sondaggio delibera chiuso)
  const inviaEsito = async (id) => {
    if (!window.confirm("Inviare a tutti i destinatari l'email di riepilogo esito del sondaggio?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/raccolte/${id}/invia-esito`);
      toast.success(`Email di esito inviate: ${data.email_inviate} destinatari su ${data.aventi_diritto}`);
      // Ricarica il dettaglio così l'admin vede subito eventuali flag di stato
      const r = await api.get(`/raccolte/${id}`);
      setSel(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Invio non riuscito");
    } finally { setBusy(false); }
  };

  const fmt = (s) => { if (!s) return ""; try { return new Date(s).toLocaleDateString("it-IT"); } catch { return s; } };

  if (sel) {
    const condomini = [...new Set(sel.destinatari.filter((d) => d.tipo === "condomino").map((d) => d.condominio_nome).filter(Boolean))].sort();
    const rows = sel.destinatari.filter((d) => (!fTipo || d.tipo === fTipo) && (!fCond || d.condominio_nome === fCond));
    // Iter122aa · statistiche sondaggio delibera
    const isSond = sel.tipo === "sondaggio_delibera";
    let stats = null;
    if (isSond) {
      const votanti = sel.destinatari.filter((d) => d.risposto);
      const cnt = (v) => votanti.filter((d) => (d.risposte || {}).voto === v).length;
      const app = cnt("Approvo");
      const nonapp = cnt("Non approvo");
      const ast = cnt("Astenuto");
      const nonvot = sel.destinatari.length - votanti.length;
      // Iter122k36 · Regola "teste": approvato quando ad approvare è la MAGGIORANZA
      // degli aventi diritto (non dei soli votanti). Fino all'introduzione dei millesimi.
      const tot = sel.destinatari.length;
      const soglia = Math.floor(tot / 2) + 1;  // maggioranza semplice degli aventi diritto
      let esito;
      if (app >= soglia) esito = "APPROVATO";
      else if (nonapp >= soglia) esito = "NON APPROVATO";
      else esito = "MAGGIORANZA NON RAGGIUNTA";
      stats = { app, nonapp, ast, nonvot, tot, soglia, esito, votanti: votanti.length };
    }
    const scaricaVerbale = async () => {
      try {
        const res = await api.get(`/raccolte/${sel.id}/verbale.pdf`, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a"); a.href = url;
        a.download = `verbale_sondaggio_${sel.id.slice(0, 8)}.pdf`;
        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      } catch { toast.error("Impossibile scaricare il verbale"); }
    };
    return (
      <div data-testid="raccolta-dettaglio">
        <button onClick={() => setSel(null)} className="flex items-center gap-1 text-brand mb-3 font-semibold" data-testid="raccolta-back"><ChevronLeft className="w-4 h-4" /> Tutte le raccolte</button>
        <h1 className="font-display text-2xl font-bold flex items-center gap-2">{isSond && <Vote className="w-6 h-6 text-indigo-700" />} {sel.titolo}</h1>
        <p className="text-sm text-slate-500 mb-3">Creata il {fmt(sel.created_at)} · <b className="text-green-700">{sel.risposte}</b> risposte su {sel.totale} destinatari{sel.data_scadenza && <> · Scadenza voto: <b>{sel.data_scadenza}</b></>}</p>

        {/* Iter122aa · pannello statistiche sondaggio */}
        {isSond && stats && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4" data-testid="sondaggio-stats">
            <div className="p-3 rounded-lg border-2 border-green-300 bg-green-50 text-center">
              <div className="text-xs font-bold uppercase tracking-wide text-green-800">Approvo</div>
              <div className="text-2xl font-extrabold text-green-800">{stats.app}</div>
            </div>
            <div className="p-3 rounded-lg border-2 border-red-300 bg-red-50 text-center">
              <div className="text-xs font-bold uppercase tracking-wide text-red-800">Non approvo</div>
              <div className="text-2xl font-extrabold text-red-800">{stats.nonapp}</div>
            </div>
            <div className="p-3 rounded-lg border-2 border-amber-300 bg-amber-50 text-center">
              <div className="text-xs font-bold uppercase tracking-wide text-amber-800">Astenuto</div>
              <div className="text-2xl font-extrabold text-amber-800">{stats.ast}</div>
            </div>
            <div className="p-3 rounded-lg border-2 border-slate-300 bg-slate-50 text-center">
              <div className="text-xs font-bold uppercase tracking-wide text-slate-600">Non votanti</div>
              <div className="text-2xl font-extrabold text-slate-700">{stats.nonvot}</div>
            </div>
            <div className={`p-3 rounded-lg border-2 text-center col-span-2 sm:col-span-1 ${
              stats.esito === "APPROVATO" ? "border-green-600 bg-green-100" :
              stats.esito === "NON APPROVATO" ? "border-red-600 bg-red-100" : "border-amber-600 bg-amber-100"}`}>
              <div className="text-xs font-bold uppercase tracking-wide text-slate-700">Esito attuale</div>
              <div className={`text-lg font-extrabold ${
                stats.esito === "APPROVATO" ? "text-green-800" :
                stats.esito === "NON APPROVATO" ? "text-red-800" : "text-amber-800"}`}>{stats.esito}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">Servono {stats.soglia}/{stats.tot} voti Approvo</div>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-4">
          {isSond && (
            <Button data-testid="verbale-pdf-btn" onClick={scaricaVerbale} className="h-11 bg-indigo-700 hover:bg-indigo-800"><FileDown className="w-4 h-4 mr-2" /> Scarica verbale PDF</Button>
          )}
          {/* Iter122jl · Trigger manuale email esito (solo sondaggi delibera) */}
          {isSond && (
            <Button data-testid="invia-esito-btn" disabled={busy} onClick={() => inviaEsito(sel.id)} className="h-11 bg-emerald-700 hover:bg-emerald-800">
              <Mail className="w-4 h-4 mr-2" /> {sel.esito_email_sent_at ? "Reinvia esito" : "Invia esito ai destinatari"}
            </Button>
          )}
          <Button data-testid="raccolta-export-btn" onClick={() => esporta(sel.id, sel.titolo)} className="h-11 bg-[#1C7C54] hover:bg-[#155c3e]"><Download className="w-4 h-4 mr-2" /> Esporta Excel</Button>
          <Button data-testid="raccolta-sollecita-btn" variant="outline" disabled={busy} onClick={() => sollecita(sel.id)} className="h-11"><Bell className="w-4 h-4 mr-2" /> Sollecita mancanti</Button>
          <Button data-testid="raccolta-elimina-btn" variant="outline" onClick={() => elimina(sel.id)} className="h-11 border-red-300 text-red-700"><Trash2 className="w-4 h-4 mr-2" /> Elimina</Button>
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          <select value={fTipo} onChange={(e) => setFTipo(e.target.value)} className="h-11 px-3 border-2 border-slate-300 rounded-lg bg-white" data-testid="raccolta-filtro-tipo">
            <option value="">Tutti i destinatari</option><option value="condomino">Condòmini</option><option value="fornitore">Fornitori</option>
          </select>
          {condomini.length > 0 && (
            <select value={fCond} onChange={(e) => setFCond(e.target.value)} className="h-11 px-3 border-2 border-slate-300 rounded-lg bg-white" data-testid="raccolta-filtro-condominio">
              <option value="">Tutti i condomìni</option>{condomini.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
        </div>
        <Card className="overflow-x-auto border-2">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left p-2">Stato</th><th className="text-left p-2">Nome</th>
                <th className="text-left p-2">Condominio / Categoria</th>
                {(sel.campi || []).map((c) => <th key={c.id} className="text-left p-2">{c.etichetta}</th>)}
                <th className="text-left p-2">Anagrafica</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id} className="border-t" data-testid={`raccolta-row-${d.id}`}>
                  <td className="p-2 whitespace-nowrap">
                    {d.risposto ? (
                      <div className="flex items-center gap-1.5">
                        <CheckCircle2 className="w-4 h-4 text-green-600" title="Risposto" />
                        {/* Iter122ji · mostra QUANDO ha risposto */}
                        {d.risposto_at && (
                          <span className="text-xs text-green-700 font-semibold" data-testid={`raccolta-risposto-at-${d.id}`}>
                            {(d.risposto_at || "").slice(0, 10).split("-").reverse().join("/")}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-amber-600"><Clock className="w-4 h-4" title="In attesa" /> <span className="text-xs">In attesa</span></span>
                    )}
                  </td>
                  <td className="p-2 font-semibold">{d.nome}</td>
                  <td className="p-2 text-slate-600">{d.condominio_nome || d.categoria || "—"}</td>
                  {(sel.campi || []).map((c) => <td key={c.id} className="p-2">{(d.risposte || {})[c.id] || ""}</td>)}
                  <td className="p-2">
                    {sel.tipo === "rac" ? (
                      d.risposto ? <span className="inline-flex items-center gap-1 text-xs text-green-700 font-semibold"><CheckCircle2 className="w-3.5 h-3.5" /> In anagrafica</span> : <span className="text-xs text-slate-400">In attesa</span>
                    ) : d.risposto ? (
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="outline" className="h-8 text-xs border-brand/40 text-brand" data-testid={`raccolta-applica-${d.id}`} onClick={() => openApply(d)}>
                          <UserCheck className="w-3.5 h-3.5 mr-1" /> Applica
                        </Button>
                        {d.applicato_at && <CheckCircle2 className="w-4 h-4 text-green-600" title={`Applicato ${(d.applicato_at || "").slice(0, 10)}`} />}
                      </div>
                    ) : <span className="text-xs text-slate-400">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <Dialog open={!!applyDest} onOpenChange={(o) => { if (!o) { setApplyDest(null); setApplyMeta(null); } }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="raccolta-applica-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><UserCheck className="w-5 h-5 text-brand" /> Applica all'anagrafica — {applyDest?.nome}</DialogTitle></DialogHeader>
            {applyMeta && !applyMeta.record_found ? (
              <div className="p-4 bg-amber-50 text-amber-800 rounded-lg text-sm">Nessuna scheda anagrafica collegata a questo destinatario (potrebbe essere stata eliminata). Impossibile applicare.</div>
            ) : applyMeta ? (
              <div className="space-y-3">
                <p className="text-sm text-slate-500">Per ogni risposta scegli il campo dell'anagrafica {applyMeta.tipo === "fornitore" ? "fornitore" : "condòmino"} da aggiornare. I campi lasciati su "Ignora" non verranno modificati.</p>
                {(sel.campi || []).filter((c) => String((applyDest?.risposte || {})[c.id] || "").trim() !== "").map((c) => {
                  const val = (applyDest?.risposte || {})[c.id] || "";
                  const target = mapping[c.id] || "";
                  return (
                    <div key={c.id} className="grid sm:grid-cols-[1fr_1fr] gap-2 items-center border rounded-lg p-3 bg-slate-50">
                      <div className="min-w-0">
                        <div className="text-xs text-slate-500">{c.etichetta}</div>
                        <div className="font-semibold text-slate-800 break-words">{val}</div>
                      </div>
                      <div className="min-w-0">
                        <select data-testid={`raccolta-map-${c.id}`} value={target} onChange={(e) => setMapping((m) => ({ ...m, [c.id]: e.target.value }))}
                          className="w-full h-10 border-2 border-slate-300 rounded-lg px-2 bg-white text-sm">
                          <option value="">— Ignora —</option>
                          {Object.entries(applyMeta.fields).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                        </select>
                        {target && <div className="text-xs text-slate-400 mt-1 truncate">Attuale: {applyMeta.current[target] || "(vuoto)"}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setApplyDest(null); setApplyMeta(null); }} className="h-11">Annulla</Button>
              {applyMeta?.record_found && (
                <Button data-testid="raccolta-applica-conferma" disabled={applying} onClick={applyNow} className="h-11 bg-brand hover:bg-brand-dark">
                  <UserCheck className="w-4 h-4 mr-2" /> Aggiorna anagrafica
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div data-testid="raccolta-dati-page">
      <div className="flex items-center gap-2 mb-1"><ClipboardList className="w-6 h-6 text-brand" /><h1 className="font-display text-2xl font-bold">Raccolta dati</h1></div>
      <p className="text-sm text-slate-500 mb-4">Crea e avvia le tue campagne dai modelli qui sotto, poi consulta le raccolte avviate per vedere le risposte, sollecitare i mancanti ed esportare in Excel.</p>

      <ComunicazioniModelli />

      <div className="flex items-center gap-2 mt-8 mb-1"><ClipboardList className="w-5 h-5 text-brand" /><h2 className="font-display text-lg font-bold">Raccolte avviate</h2></div>
      <p className="text-sm text-slate-500 mb-3">Le campagne di raccolta dati avviate dai modelli "Richiesta dati" e le <b>Campagne RAC</b> (anagrafe condominiale). Apri una campagna per vedere chi ha compilato, sollecitare i mancanti ed esportare in Excel.</p>
      {list.length === 0 ? (
        <Card className="p-8 text-center text-slate-500 border-2">Nessuna raccolta avviata. Usa il riquadro <b>Modelli di comunicazione e moduli</b> qui sopra: crea un modello "Richiesta dati" e premi <b>Avvia raccolta dati</b>, oppure avvia una <b>Campagna RAC</b>.</Card>
      ) : (
        <div className="space-y-2">
          {list.map((r) => (
            <Card key={r.id} className="p-4 border-2 flex items-center justify-between gap-3 cursor-pointer hover:border-brand/40" onClick={() => apri(r.id)} data-testid={`raccolta-item-${r.id}`}>
              <div className="min-w-0">
                <div className="font-semibold truncate">{r.titolo}</div>
                <div className="text-xs text-slate-500">{fmt(r.created_at)} · {r.destinatari} destinatari</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-lg font-bold text-green-700">{r.risposte}/{r.destinatari}</div>
                <div className="text-xs text-slate-400">risposte</div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
